import { readonly, ref } from 'vue';
import type { ApiErrorPayload } from '@shared/api';
import { FILTER_TEMPLATE_HEADER, getActiveFilterTemplateId } from './filter-template';

const TOKEN_KEY = 'token';
let authEpoch = 0;
const pendingRequests = ref(0);
export const networkPendingRequests = readonly(pendingRequests);

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload: ApiErrorPayload,
  ) {
    super(payload.error || payload.message || payload.msg || `请求失败（${status}）`);
  }
}

function getToken(): string | null {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function saveToken(token: string | null): void {
  authEpoch += 1;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  /*
   * 1.1.78: 全局筛选 —— 用户选中的「筛选模板」id 随每个请求带上，
   * 后端把它与本人数据范围取交集后再过滤响应（只窄不宽）。没选就不带。
   */
  const filterTemplateId = getActiveFilterTemplateId();
  if (filterTemplateId) headers.set(FILTER_TEMPLATE_HEADER, String(filterTemplateId));
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  pendingRequests.value += 1;
  try {
    const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) {
      throw new HttpError(response.status, typeof payload === 'object' ? payload : { error: String(payload) });
    }
    return payload as T;
  } finally {
    pendingRequests.value = Math.max(0, pendingRequests.value - 1);
  }
}

const inflightGets = new Map<string, Promise<unknown>>();

function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  // A caller-owned AbortSignal must not be shared: aborting it would cancel every
  // consumer of the same request. Ordinary GETs are safe to coalesce, which also
  // mirrors the legacy app's shared data load when a page opens during preloading.
  if (signal) return request<T>(path, { signal });
  // 1.1.78: 缓存键要带上筛选模板 id —— 否则切换筛选后同 path 仍命中旧响应
  const key = `${authEpoch}:${getActiveFilterTemplateId() ?? 'all'}:${path}`;
  const existing = inflightGets.get(key);
  if (existing) return existing as Promise<T>;
  const pending = request<T>(path);
  inflightGets.set(key, pending);
  const clear = () => {
    if (inflightGets.get(key) === pending) inflightGets.delete(key);
  };
  void pending.then(clear, clear);
  return pending;
}

export const http = {
  get,
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, body === undefined ? { method: 'POST' } : { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, body === undefined ? { method: 'PUT' } : { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
